import { useState, useCallback, useMemo, Fragment } from 'react'
import { ComposerFacebookPost, FBAlbum } from 'shared/types'
import styles from './CustomizePostBlock.pcss'
import Chip from '@mui/material/Chip'
import { PPDialog } from 'components/PPDialog'
import { FormattedMessage } from 'react-intl'
import FacebookAlbumSelector from '../AlbumSelector/FacebookAlbumSelector'
import { EmptyNetworkPostOptions } from './EmptyNetworkPostOptions'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import { mdiShareVariant, mdiImageAlbum } from '@mdi/js'
import Icon from '@mdi/react'
import { FB_POST_TYPE_REEL, POST_TYPE_STORY } from 'shared/constants'
import { useSelector } from 'react-redux'
import { composerImageUrlsSelector } from 'services/compose/selectors'
import PPSwitch from 'components/PPSwitch'
import { PostDestination } from 'shared'

interface ComposerFacebookPostOptionsProps {
  post: ComposerFacebookPost
  profiles: PostDestination[]
  profile: PostDestination | null
  withImage: boolean
  withReshare: boolean
  empty?: boolean
  withVideo?: boolean
  hideEmpty?: boolean
  selectedAlbumsCustom?: FBAlbum[]
  onToggleReshare?: () => void
  onSelectedAlbumChangeCustom?: (profile: PostDestination, album: FBAlbum | undefined) => void
  onPostTypeChange: (postType: string) => void
}

export function ComposerFacebookPostOptions(props: ComposerFacebookPostOptionsProps) {
  const { onPostTypeChange, onToggleReshare } = props
  const imageUrls = useSelector(composerImageUrlsSelector)
  const [albumsPopupActive, setAlbumsPopupActive] = useState(false)

  const onReshareValueChange = useCallback((value: 'new' | 'reshare') => {
    const nextReshare = value === 'reshare'
    if (nextReshare !== props.post.reshare && onToggleReshare) {
      onToggleReshare()
    }
  }, [props.post.reshare, onToggleReshare])

  const postTypeOptions = useMemo(() => {
    const options = [{
      value: 'post',
      label: 'Post'
    }]
    if (props.withVideo) {
      options.push({
        value: FB_POST_TYPE_REEL,
        label: 'Reel'
      }, {
        value: POST_TYPE_STORY,
        label: 'Story'
      })
    } else if (props.withImage && imageUrls.length === 1) {
      options.push({
        value: POST_TYPE_STORY,
        label: 'Story'
      })
    }
    return options
  }, [props.withVideo, props.withImage, imageUrls.length])

  if (props.empty) {
    return props.hideEmpty ? null : <EmptyNetworkPostOptions />
  }

  const profile = props.profile
  const showAlbums = props.withImage && !props.post.reshare
  const withAlbumsPopup = Boolean(showAlbums && !props.profile)
  const withAlbums = showAlbums && profile

  const openAlbumsPopup = () => {
    setAlbumsPopupActive(true)
  }

  const closeAlbumsPopup = () => {
    setAlbumsPopupActive(false)
  }

  const options: PPSelectOptions = {
    new: { label: <FormattedMessage id="composer.labels.photos.new-post" /> },
    reshare: { label: <FormattedMessage id="composer.labels.photos.reshare" /> }
  }

  return (
    <Fragment>
      {postTypeOptions.length > 1 && (
        <div className={styles['option-box']}>
          <PPSwitch
            selectedValue={props.post.postType || 'post'}
            options={postTypeOptions}
            onSelectedValueChange={onPostTypeChange}
          />
        </div>
      )}
      {props.withReshare && (
        <div className={styles['option-box']}>
          <PPSelect
            name="Post as"
            icon={<Icon path={mdiShareVariant} size="16px" />}
            options={options}
            withCaret
            selectedValue={props.post.reshare ? 'reshare' : 'new'}
            labelClassName={styles['select-label']}
            onSelectionChange={onReshareValueChange}
          />
        </div>
      )}
      {withAlbums && (
        <div className={styles['option-box']} key={profile.id}>
          <FacebookAlbumSelector
            hideProfileImage
            profile={profile}
            selectedAlbumsCustom={props.selectedAlbumsCustom}
            onSelectedAlbumChangeCustom={props.onSelectedAlbumChangeCustom}
          />
        </div>
      )}
      {withAlbumsPopup && (
        <Fragment>
          <Chip
            label={
              <FormattedMessage
                id={props.post.albums.length === 0 ? 'composer.labels.fb-album-default' : 'composer.labels.fb-album-custom'}
              />
            }
            avatar={<Icon path={mdiImageAlbum} size="20px" />}
            classes={{ root: styles.chip, label: styles.label }}
            onClick={openAlbumsPopup}
          />
          <PPDialog
            open={albumsPopupActive}
            onClose={closeAlbumsPopup}
            size="lg"
            title={<FormattedMessage id="composer.labels.select-multiple-albums" />}
          >
            <div className={styles['popup-body']}>
              <div className={styles['albums-popup-content']}>
                {props.profiles.map(profile => (
                  <div className={styles['option-box']} key={profile.id}>
                    <FacebookAlbumSelector
                      profile={profile}
                      raised
                      selectedAlbumsCustom={props.selectedAlbumsCustom}
                      onSelectedAlbumChangeCustom={props.onSelectedAlbumChangeCustom}
                    />
                  </div>
                ))}
              </div>
            </div>
          </PPDialog>
        </Fragment>
      )}
    </Fragment>
  )
}
